import {ChangeDetectorRef, Component, inject, Input, OnChanges, OnDestroy, OnInit, signal, SimpleChanges, WritableSignal} from "@angular/core";
import {CmsApiService} from "../../../core/cms-api.service";
import {PrimusRouterService} from "../../../core/primus-router.service";
import {Subscription} from "rxjs";
import {ActivatedRoute} from "@angular/router";
import {GetArtifactsParams} from "../../../core/definitions/get-artifacts-params";
import {Field, ReportUserGenerated} from "../../../core/model-store/models/report-user-generated";
import {ModelStore} from "../../../core/model-store/model-store";
import {GetArtifactViewAndDataForReportParams} from "../../../core/definitions/get-artifact-params";
import {ObjectViewAndData} from "../../../core/definitions/object-view";
import { getDefaultViewOptionsCheckboxValues, reportOverviewFieldOptions } from '../generate-report-util'
import {stampReportUuid, gridReportUuid, katalograpportUuid, prosedyrerapportUuid, rapportlisteMedBildeUuid} from "../../../core/model-store/models/report-view-type";
import {UrlQueryParam} from "../../../core/model-store/url-query-param";
import {HibernationService} from "../../../shared/hibernation.service";
import {MediaHelperService} from "../../../core/media-helper.service";
import {fetchReportImageUrls, setThumbnailUrls, thumbnailImageIds} from "./report-images";

/** These report types show object images and say when they have loaded. */
const reportTypesWithImages = [katalograpportUuid, gridReportUuid, stampReportUuid, rapportlisteMedBildeUuid];

@Component({
    selector: 'report-preview',
    templateUrl: './report-preview.component.html',
    styleUrls: ['./report-preview.component.scss'],
    standalone: false
})
export class ReportPreviewComponent implements OnInit, OnChanges, OnDestroy {
  // Injections
  private readonly cdRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly cms: CmsApiService = inject(CmsApiService);
  private readonly hibernationService: HibernationService = inject(HibernationService);
  private readonly mediaHelper: MediaHelperService = inject(MediaHelperService);
  private readonly modelStore: ModelStore = inject(ModelStore);
  private readonly primusRouter: PrimusRouterService = inject(PrimusRouterService);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);

  private paramMapSubscription: Subscription;
  reportId: string;
  @Input() limit: number;
  artifactsFromReport: ObjectViewAndData[];
  @Input() reportTemplate: ReportUserGenerated;
  @Input() viewOptionsCheckboxValues: {key: string, label: string, value: any, requiresUnchecked?: string, requiresChecked?: string}[];
  @Input() preview: boolean = false;
  @Input() numPhotographsInReport: number = 1;
  @Input() numColumnsInReport: number = 2;
  @Input() selectedPhotographSize: string = 'small';
  showReportLoadingInfo: WritableSignal<boolean> = signal(false);
  /** Set when the report could not be loaded; the PDF generator then stops. */
  loadError: WritableSignal<string | null> = signal(null);
  extractedArtifacts: any;
  private imagesReady: WritableSignal<boolean> = signal(false);
  private thumbnailUrls = new Map<string, string>();

  ngOnInit() {
    this.hibernationService.disableHealthCheck();
    this.asyncInit().then();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.reportTemplate) {
      const previousFieldSections = changes.reportTemplate?.previousValue?.fieldSections;
      const currentFieldSections = changes.reportTemplate?.currentValue?.fieldSections;

      const previousIsSelectedValues = previousFieldSections ? previousFieldSections.map(section => section.isSelected).join(', ') : '';
      const currentIsSelectedValues = currentFieldSections ? currentFieldSections.map(section => section.isSelected).join(', ') : '';

      console.log('PREVIOUS IS SELECTED VALUES: ', previousIsSelectedValues);
      console.log('CURRENT IS SELECTED VALUES: ', currentIsSelectedValues);

      if (this.extractedArtifacts) {
        this.extractedArtifacts = this.extractFieldsForReportTemplate(this.reportTemplate, this.artifactsFromReport);
        setThumbnailUrls(this.extractedArtifacts, this.thumbnailUrls);
      }

      this.cdRef.detectChanges();

    }
  }

  ngOnDestroy() {
    this.hibernationService.enableHealthCheck();
  }

  /**
   * The PDF generator prints the page once #reportdone exists. Reports with
   * images must wait until they have loaded, or large reports are printed
   * with images missing.
   */
  isReportDone(): boolean {
    return !reportTypesWithImages.includes(this.reportTemplate.reportViewTypeId) ||
      this.imagesReady();
  }

  onImagesReady() {
    this.imagesReady.set(true);
  }

  private async asyncInit() {
    this.showReportLoadingInfo.set(true);
    try {
      await this.loadReport();
    } catch (e) {
      console.error('Failed to load the report preview', e);
      this.loadError.set(e?.message ?? String(e));
    } finally {
      this.showReportLoadingInfo.set(false);
    }
  }

  private async loadReport() {
    console.log('ROUTE: ', this.primusRouter.currentState());

    let reportViewTypes = this.modelStore.findAllModels('report_view_type');
    await reportViewTypes.loading;

    this.paramMapSubscription = this.route.paramMap.subscribe(params => {
      this.reportId = params.get('report_id');
    });

    if (!this.reportTemplate) {
      this.reportTemplate = this.modelStore.findModel('report_user_generated', this.reportId, new UrlQueryParam('timezone_offset', '' + new Date().getTimezoneOffset()));
      await this.reportTemplate.loading;
    }

    if (this.reportId) {
      await this.reportTemplate.loading;

      let reportViewType = this.modelStore.findModel('report_view_type', this.reportTemplate.reportViewTypeId);
      await reportViewType.loading;

      console.log('REPORT VIEW TYPE', reportViewType);
      console.log('REPORT TEMPLATE: ', this.reportTemplate);
      let params: GetArtifactsParams = new GetArtifactsParams();
      params.artifact_ids = this.reportTemplate?.getArtifactIds ? this.reportTemplate.getArtifactIds() : [];
      params.get_source = true;

      let artifactPrams: GetArtifactViewAndDataForReportParams = new GetArtifactViewAndDataForReportParams();
      artifactPrams.report_id = this.reportId;
      artifactPrams.get_original_artifact = true;
      artifactPrams.compact = true;
      if (this.limit) {
        artifactPrams.limit = this.limit;
      }
      this.artifactsFromReport = await this.cms.gerArtifactViewAndDataForReport(artifactPrams);
      this.extractedArtifacts = this.extractFieldsForReportTemplate(this.reportTemplate, this.artifactsFromReport);
      await this.loadThumbnailUrls(this.extractedArtifacts);
      if (!this.preview) {
        // Only the edit view extracts again; the printed report can free the
        // raw data, which matters for reports of thousands of objects.
        this.artifactsFromReport = null;
      }

      console.log('report preview: ', this.viewOptionsCheckboxValues);
      if (!this.viewOptionsCheckboxValues) {
        this.viewOptionsCheckboxValues =  getDefaultViewOptionsCheckboxValues();
      }

      if (this.reportTemplate.reportViewOptions) {
        Object.keys(this.reportTemplate.reportViewOptions).forEach(option => {
          if (option === 'numPhotosPerObject') {
            this.numPhotographsInReport = this.reportTemplate.reportViewOptions[option];
          }

          if (option === 'numColumnsInReport') {
            this.numColumnsInReport = this.reportTemplate.reportViewOptions[option];
          }

          if (option === 'photographSize') {
            this.selectedPhotographSize = this.reportTemplate.reportViewOptions[option];
          }

          let checkbox = this.viewOptionsCheckboxValues.find(viewOption => viewOption.key === option);
          if (checkbox) {
            checkbox.value = this.reportTemplate.reportViewOptions[option];
          }
        })
      }
    }
  }

  /**
   * Fetches the thumbnail URLs of all objects in one request before the
   * report is shown, so app-thumbnail does not ask for them one by one.
   */
  private async loadThumbnailUrls(artifacts: any[]) {
    const urls = await fetchReportImageUrls(this.mediaHelper, thumbnailImageIds(artifacts), 'thumbnail');
    urls.forEach((url, imageId) => this.thumbnailUrls.set(imageId, url));
    setThumbnailUrls(artifacts, this.thumbnailUrls);
  }

  /**
   * This function is rather ugly, but it is used to extract the fields from the artifacts that are needed for the report
   * The fields are extracted based on the sections that are selected in the report template, as well as the
   * fields that are selected in the sections.
   *
   * @param reportTemplate
   * @param artifacts
   */
  extractFieldsForReportTemplate(reportTemplate: ReportUserGenerated, artifacts: ObjectViewAndData[]): any {
    let objectFields: {}[] = [];

    artifacts.forEach(objectView => {
      let objectForReport = {
        artifactId: objectView.artifact.artifact_id,
        artifactName: objectView.artifact.artifact_name,
        summaryIdentifier: objectView.view_data.summary_identifier,
        summaryTitle: objectView.view_data.summary_title,
        sections: []
      };

      // Add thumbnail image to objectForReport
      //TODO: Check if there is a thumbnail image, and use that image ID instead of the first image in the array
      if (objectView.artifact.images && objectView.artifact.images.length > 0) {
        objectForReport['thumbnailImage'] = objectView.artifact.images[0];
        if (objectView.artifact.images && objectView.artifact.images.length > 0 && objectView.artifact.images[0].image_id) {
          objectForReport['thumbnailImage']['thumbnail_id'] = objectView.artifact.images[0].image_id;
        }

        objectForReport['images'] = [];
        objectView.artifact.images.forEach(image => {
          objectForReport['images'].push({
            image_id: image.image_id,
            dms_identifier: image.dms_identifier,
            object_type: image.object_type,
            thumbnail_id: image.image_id
          });
        });
      }

      const overviewSection = this.getOverviewSection(reportTemplate, objectView);
      if (overviewSection) {
        objectForReport.sections.push(overviewSection);
      }

      if (reportTemplate.reportViewTypeId === stampReportUuid) {
        if (reportTemplate.fieldSections) {
          reportTemplate.fieldSections.forEach(section => {
            // If section is selected, add the section from view_data to objectForReport
            let foundSection = objectView.view_data.sections.find(sectionView => sectionView.name === section.name);
            if (foundSection && foundSection.name === 'motif') {
              objectForReport['sections'].push(foundSection);
              console.log('-------- ', section.name);
              console.log(section.fields);
              let reportFields = section.fields
                .filter(field => field.name === 'motif_description')
                .map(field => {
                  return this.formatFields(foundSection, field);
                });
              if (reportFields.length > 0) { // Only add sections that have fields with values
                objectForReport.sections.push({
                  id: section.name,
                  title: section.title,
                  fields: reportFields
                });
              }
            }
          });
        }
      } else if (reportTemplate.fieldSections) {
        reportTemplate.fieldSections.forEach(section => {
          // If section is selected, add the section from view_data to objectForReport
          let foundSection = objectView.view_data.sections.find(sectionView => sectionView.name === section.name);
          if (section.isSelected && foundSection) {
            let reportFields = section.fields
              .filter(field => field.isSelected)
              .map(field => {
                return this.formatFields(foundSection, field);
              })
              .filter(field => field !== null); // Remove any null fields

            if (reportFields.length > 0) { // Only add sections that have fields with values
              objectForReport.sections.push({
                id: section.name,
                title: section.title,
                fields: reportFields
              });
            }
          }
        });
      }

      objectFields.push(objectForReport);
    });

    return objectFields;
  }

  /**
   * Fields from the object overview that the report's view options ask for,
   * such as current placement and current condition code. The backend leaves
   * out fields the user has no rights to see.
   */
  private getOverviewSection(reportTemplate: ReportUserGenerated, objectView: ObjectViewAndData) {
    const fieldNames = reportOverviewFieldOptions
      .filter(fieldOption => reportTemplate.reportViewOptions?.[fieldOption.option])
      .map(fieldOption => fieldOption.fieldName);
    if (fieldNames.length === 0) {
      return null;
    }

    const fields = fieldNames
      .map(fieldName => (objectView.view_data.overview ?? []).find(overviewField =>
        overviewField.field_name === fieldName || overviewField.field_name.startsWith(fieldName + '.')))
      .filter(overviewField => !!overviewField)
      .map(overviewField => ({
        key: overviewField.title,
        fieldName: overviewField.field_name,
        value: (overviewField.items ?? [])
          .map(item => ({
            values: item.field_values?.values?.map(value => value.value).join(' ') ?? '',
            labels: ''
          }))
          .filter(value => value.values)
      }))
      .filter(field => field.value.length > 0);

    return fields.length > 0 ? {id: 'overview', title: 'TRANS__OVERVIEW_MENU__OVERVIEW', fields} : null;
  }

  private formatFields(foundSection, field: Field) {
    // Find the corresponding sectionField in foundSection.section_fields
    let sectionField = foundSection.section_fields.find(
      sectionField => field.path ? sectionField.field_name === field.path + "." + field.name : sectionField.field_name.endsWith("." + field.name) || sectionField.field_name === field.name || sectionField.field_name.endsWith(field.name + ".description")
    );

    if (sectionField) {
      // Map the field to a key-value pair for the UI to use
      let reportField = {key: sectionField.title, fieldName: sectionField.field_name, value: []};

      if (sectionField.items) {
        sectionField.items.forEach(item => {
          let sectionFieldItem = {};

          sectionFieldItem['values'] = item.field_values?.values?.map(value => value.value).join(" ") ?? "";
          sectionFieldItem['labels'] = item.field_values?.title_values?.map(value => value.value).join(" ") ?? "";
          if (item.header_values && item.header_values.level === 1) {
            sectionFieldItem['headersLevel1'] = item.header_values?.values?.map(value => value.value).join(" ") ?? "";
            reportField['hasHeader1Or2'] = true;
          } else if (item.header_values && item.header_values.level === 2) {
            sectionFieldItem['headersLevel2'] = item.header_values?.values?.map(value => value.value).join(" ") ?? "";
            reportField['hasHeader1Or2'] = true;
          }

          reportField.value.push(sectionFieldItem);
        });
      }

      // Only return the reportField if it has a value
      return reportField.value.length > 0 ? reportField : null;
    } else {
      return null; // Field not found, return null
    }
  }

  protected readonly katalograpportUuid = katalograpportUuid;
  protected readonly gridReportUuid = gridReportUuid;
  protected readonly stampReportUuid = stampReportUuid;
  protected readonly prosedyrerapportUuid = prosedyrerapportUuid;
  protected readonly rapportlisteMedBildeUuid = rapportlisteMedBildeUuid;
}
